import {
  CLOUD_CONSTRUCT_LINK_STATE_KEY,
  type CloudConstructLinkPayload,
  type CloudConstructLinkState,
  type NodeGraphExtra,
  type NodeSn,
  cloudConstructNodeTypeFromVendor,
} from '@ipfabric/utils';
import type { DeviceViewModel } from 'components/DeviceView/model/DeviceViewModel';
import { showNodeDetail } from 'components/DeviceView/model/commands/showNodeDetail';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const showCloudConstruct = (
  deviceView: DeviceViewModel,
  {
    constructKind,
    constructId,
    vendor,
    name,
    extra,
  }: CloudConstructLinkPayload,
): void => {
  const nodeType = cloudConstructNodeTypeFromVendor(vendor, constructKind);
  if (nodeType === null) {
    return;
  }

  // Some constructs (e.g. GCP peerings) have no own id; identify them by
  // the composite of their extra values instead, so the entity key stays
  // unique per construct and the explorer resolves detail/filters from
  // `extra` — the same way the diagram does.
  const sn = constructId || Object.values(extra).join(':');

  deviceView.dispatch(showNodeDetail, {
    // `vendor` plus the extra fields drive the explorer's detail tab and
    // sub-table filters. The construct name is the single source for both
    // the tab title and the detail panel's name field, so it is injected
    // here rather than carried per-construct.
    extra: {
      vendor,
      ...(name ? { cloudConstructName: name } : {}),
      ...extra,
    } as NodeGraphExtra,
    nodeType,
    // The device-view entity is still keyed on the legacy `sn` field; for a
    // cloud construct the construct id (or composite fallback) plays that role.
    sn: sn as NodeSn,
    title: name || constructId || sn,
  });
};

/**
 * Opens the Device Explorer for the cloud construct handed over in
 * `location.state` by a `cloudConstruct` column link (see
 * `createCloudConstructLink`), because the Explorer is in-page state with no
 * URL of its own — the same hand-off the `cloudSecurityTree` link uses.
 *
 * Unlike a modal the Explorer stays open while the user keeps browsing, so its
 * open state lives in `deviceView` and the router state is only a one-shot
 * message: it is consumed and cleared right away, which also lets the same cell
 * be clicked again.
 */
export const useCloudConstructExplorer = (
  deviceView: DeviceViewModel,
): void => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const payload = (
      location.state as Partial<CloudConstructLinkState> | null | undefined
    )?.[CLOUD_CONSTRUCT_LINK_STATE_KEY];

    if (payload) {
      navigate(location.pathname, { replace: true, state: null });
      showCloudConstruct(deviceView, payload);
    }
    // Only `location` really drives this; `deviceView` is a stable model and
    // the dispatch is gated on a payload being present anyway.
  }, [location, deviceView]);
};
